import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle, User, Mail, Phone, MessageSquare, Users, Target, Heart } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import SEO from './SEO';
import config from '../utils/config';

const API_URL = config.url;

export default function JoinUsPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    areaOfInterest: '',
    availability: '',
    skills: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [recaptchaToken, setRecaptchaToken] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setForm(prev => ({ ...prev, [name]: digitsOnly }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    setError('');
    setSuccess('');
  };

  const handleRecaptcha = (token) => {
    setRecaptchaToken(token);
    if (errors.recaptcha) setErrors(prev => ({ ...prev, recaptcha: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'नाम आवश्यक है';
    if (!form.email.trim()) newErrors.email = 'ईमेल आवश्यक है';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'मान्य ईमेल पता दर्ज करें';
    if (!form.phone.trim()) newErrors.phone = 'फोन नंबर आवश्यक है';
    else if (!/^\d{10}$/.test(form.phone.trim())) newErrors.phone = 'मान्य 10 अंकों का फोन नंबर दर्ज करें';
    if (!form.message.trim()) newErrors.message = 'संदेश आवश्यक है';
    else if (form.message.trim().length < 10) newErrors.message = 'संदेश कम से कम 10 अक्षर का होना चाहिए';
    if (!recaptchaToken) newErrors.recaptcha = 'कृपया reCAPTCHA पूरा करें';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const volunteerBlock = `\n\n— स्वयंसेवक विवरण —\nशहर: ${form.city || '-'}\nरुचि का क्षेत्र: ${form.areaOfInterest || '-'}\nउपलब्धता: ${form.availability || '-'}\nकौशल: ${form.skills || '-'}`;
      const finalMessage = `${form.message}${volunteerBlock}`;
      const response = await fetch(`${API_URL}/joinus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, email: form.email, phone: form.phone, message: finalMessage, recaptchaToken })
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message || 'आपका संदेश सफलतापूर्वक भेज दिया गया है।');
        setForm({ name: '', email: '', phone: '', city: '', areaOfInterest: '', availability: '', skills: '', message: '' });
        setRecaptchaToken('');
        if (window.grecaptcha) {
          window.grecaptcha.reset();
        }
      } else {
        setError(data.error || 'भेजने में विफल');
      }
    } catch (error) {
      setError('नेटवर्क त्रुटि। कृपया पुनः प्रयास करें।');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Users,
      title: 'समुदाय',
      description: 'हमारे समुदाय का हिस्सा बनें और साथ मिलकर समाज के विकास में योगदान दें।'
    },
    {
      icon: Target,
      title: 'लक्ष्य',
      description: 'देश के विकास के लिए कार्य करते हुए, सभी के लिए बेहतर जीवन का लक्ष्य।'
    },
    {
      icon: Heart,
      title: 'सेवा',
      description: 'स्वसुरक्षा, राष्ट्रसुरक्षा और धर्मसुरक्षा करते हुए समाज कल्याण के लिए समर्पित सेवा भावना से कार्य करें।'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100">
      <SEO 
        title="हमसे जुड़ें"
        description="श्री. संजय एच. पारदेशी के साथ जुड़ें और समाज के विकास में योगदान दें।"
        keywords="जुड़ें, संजय पारदेशी, सामाजिक सेवा, समुदाय, सीधी सोच सीधी बात"
        url="/joinus"
      />
      
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 hindi-text">
            हमसे जुड़ें
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto hindi-text">
            समाज के विकास में योगदान देने के लिए हमसे जुड़ें। साथ मिलकर बेहतर भारत बनाएं।
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          {/* Features Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 hindi-text">{feature.title}</h3>
                  <p className="text-gray-600 hindi-text">{feature.description}</p>
                </div>
              );
            })}
          </div>

          {/* Contact Form */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center hindi-text">
                संपर्क फॉर्म
              </h2>

              {/* Status Messages */}
              {success && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-800">{success}</span>
                </div>
              )}
              
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <span className="text-red-800">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2 hindi-text">
                      नाम *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${
                          errors.name ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="अपना पूरा नाम दर्ज करें"
                      />
                    </div>
                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 hindi-text">
                      ईमेल *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="अपना ईमेल पता दर्ज करें"
                      />
                    </div>
                    {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2 hindi-text">
                    फोन नंबर *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      inputMode="numeric"
                      pattern="[0-9]{10}"
                      maxLength={10}
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="अपना फोन नंबर दर्ज करें"
                    />
                  </div>
                  {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2 hindi-text">शहर</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={form.city}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors border-gray-300"
                      placeholder="अपना शहर दर्ज करें"
                    />
                  </div>
                  <div>
                    <label htmlFor="areaOfInterest" className="block text-sm font-medium text-gray-700 mb-2 hindi-text">रुचि का क्षेत्र</label>
                    <select
                      id="areaOfInterest"
                      name="areaOfInterest"
                      value={form.areaOfInterest}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors border-gray-300"
                    >
                      <option value="">चुनें</option>
                      <option value="स्वयंसेवा">स्वयंसेवा</option>
                      <option value="सोशल मीडिया">सोशल मीडिया</option>
                      <option value="कार्यक्रम आयोजन">कार्यक्रम आयोजन</option>
                      <option value="सामग्री लेखन">सामग्री लेखन</option>
                      <option value="अन्य">अन्य</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="availability" className="block text-sm font-medium text-gray-700 mb-2 hindi-text">उपलब्धता</label>
                    <select
                      id="availability"
                      name="availability"
                      value={form.availability}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors border-gray-300"
                    >
                      <option value="">चुनें</option>
                      <option value="प्रतिदिन">प्रतिदिन</option>
                      <option value="सप्ताहांत">सप्ताहांत</option>
                      <option value="सप्ताह में कुछ घंटे">सप्ताह में कुछ घंटे</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="skills" className="block text-sm font-medium text-gray-700 mb-2 hindi-text">कौशल</label>
                    <input
                      type="text"
                      id="skills"
                      name="skills"
                      value={form.skills}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors border-gray-300"
                      placeholder="अपने कौशल लिखें"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2 hindi-text">
                    संदेश *
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows="6"
                      className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors resize-none ${
                        errors.message ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="अपना संदेश यहाँ लिखें..."
                    />
                  </div>
                  {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
                </div>

                <div className="flex justify-center">
                  <ReCAPTCHA
                    sitekey="6LeXpZ8rAAAAAEjIm7QT2dc9ecIfgJYC0U5lvVXv"
                    onChange={handleRecaptcha}
                    className="mx-auto"
                  />
                </div>
                {errors.recaptcha && <p className="text-sm text-red-600 text-center">{errors.recaptcha}</p>}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>भेज रहे हैं...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>संदेश भेजें</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}